import React from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Define roles and their properties
const roles = {
  admin: { color: '#FF5733', description: 'IW' },
  user: { color: '#007bff', description: 'Attorney' },
  guest: { color: '#28a745', description: 'Employer' },
  moderator: { color: '#FFC107', description: 'Physician' },
} as const;

// Define the role type explicitly
type Role = keyof typeof roles;

// Define user location type
interface User {
  id: number;
  name: string; // Full name
  address: string;
  contactNumber: string;
  latitude: number;
  longitude: number;
  city: string;
  role: Role;
}

// User names, addresses, and contact numbers
const userDetails = [
  { name: 'Suhani Asati', address: '123, MG Road, Pune', contactNumber: '9876543210' },
  { name: 'Swapneel salunke', address: '456, Civil Lines, Nagpur', contactNumber: '9123456789' },
  { name: 'Amar ghakre', address: '789, Marine Drive, Mumbai', contactNumber: '9988776655' },
  { name: 'Saurav naik', address: '101, CP, Delhi', contactNumber: '9911223344' },
  { name: 'Raesaa nadaf', address: '202, Anna Salai, Chennai', contactNumber: '9812345678' },
  { name: 'Surajit mitra', address: '303, Park Street, Kolkata', contactNumber: '9786543210' },
  { name: 'Payal agrawal', address: '404, MG Road, Bangalore', contactNumber: '9647382910' },
  { name: 'Alik sarkar', address: '505, Pink City, Jaipur', contactNumber: '9781234567' },
  { name: 'Vishwash suryawanshi', address: '606, Banjara Hills, Hyderabad', contactNumber: '9955667788' },
  { name: 'Arvind yadav', address: '707, Gandhi Nagar, Hubli', contactNumber: '9977885566' },
];

// Sample locations for users
const locations = [
  { latitude: 18.5204, longitude: 73.8567, city: 'Pune' },
  { latitude: 21.1458, longitude: 79.0882, city: 'Nagpur' },
  { latitude: 19.076, longitude: 72.8777, city: 'Mumbai' },
  { latitude: 28.7041, longitude: 77.1025, city: 'Delhi' },
  { latitude: 13.0827, longitude: 80.2707, city: 'Chennai' },
  { latitude: 22.5726, longitude: 88.3639, city: 'Kolkata' },
  { latitude: 12.9716, longitude: 77.5946, city: 'Bangalore' },
  { latitude: 26.9124, longitude: 75.7873, city: 'Jaipur' },
  { latitude: 17.385, longitude: 78.4867, city: 'Hyderabad' },
  { latitude: 15.3173, longitude: 75.7139, city: 'Hubli' },
];

// Combine user details with locations
const userLocations: User[] = userDetails.map((user, index) => ({
  id: index + 1,
  name: user.name,
  address: user.address,
  contactNumber: user.contactNumber,
  latitude: locations[index].latitude,
  longitude: locations[index].longitude,
  city: locations[index].city,
  role: Object.keys(roles)[index % Object.keys(roles).length] as Role,
}));

const MapComponent: React.FC = () => {
  const getInitials = (name: string): string => {
    const [firstName, lastName] = name.split(' ');
    return `${firstName[0]}${lastName ? lastName[0] : ''}`.toUpperCase();
  };

  const customIcon = (role: Role, userName: string) =>
    new L.DivIcon({
      className: 'user-icon',
      html: `<div style="background-color: ${roles[role].color}; color: white; border-radius: 50%; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: bold;">${getInitials(
        userName
      )}</div>`,
      iconSize: [40, 40],
      iconAnchor: [20, 20],
    });

  return (
    <div>
      <MapContainer
        center={[20.5937, 78.9629]}
        zoom={5}
        style={{ height: '500px', width: '100%' }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a> contributors"
        />
        {userLocations.map((user) => (
          <Marker
            key={user.id}
            position={[user.latitude, user.longitude]}
            icon={customIcon(user.role, user.name)}
          >
            <Popup>
              <strong>User Name:</strong>{user.name} <br />
              <strong>Address:</strong> {user.address} <br />
              <strong>Contact:</strong> {user.contactNumber} <br />
              <strong>Role:</strong> {roles[user.role].description} <br />
              <strong>City:</strong> {user.city}
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      <div
        style={{
          marginTop: '20px',
          padding: '10px',
          backgroundColor: '#f9f9f9',
          border: '1px solid #ddd',
          borderRadius: '5px',
          maxWidth: '800px',
          margin: 'auto',
        }}
      >
        <h4 style={{ textAlign: 'center', marginBottom: '10px' }}>Expertise Details</h4>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
            gap: '10px',
          }}
        >
          {Object.entries(roles).map(([role, details]) => (
            <div
              key={role}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '5px',
                backgroundColor: '#fff',
              }}
            >
              <div
                style={{
                  backgroundColor: details.color,
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                }}
              ></div>
              <span>{details.description}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MapComponent;
